import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/zero-trust-security')({
  component: ZeroTrustPage,
  head: () => ({
    meta: [
      { title: 'Zero-Trust Security | Matricks' },
      { name: 'description', content: 'Learn about zero-trust security architecture, its principles, and how it protects modern applications. Complete guide to implementing zero-trust in your organization.' },
    ],
  }),
});

const principles = [
  {
    iconName: "Eye",
    title: "Never Trust, Always Verify",
    description: "Every request is authenticated and authorized, regardless of where it originates. No implicit trust based on network location.",
  },
  {
    iconName: "Lock",
    title: "Least Privilege Access",
    description: "Users and systems receive only the minimum permissions needed for their specific tasks, reducing the blast radius of any breach.",
  },
  {
    iconName: "Network",
    title: "Assume Breach",
    description: "Design systems assuming attackers are already inside. Implement segmentation and monitoring to detect and contain threats.",
  },
  {
    iconName: "Key",
    title: "Explicit Verification",
    description: "Verify all access requests explicitly based on all available data points including user identity, location, device, and behavior.",
  },
  {
    iconName: "AlertTriangle",
    title: "Continuous Validation",
    description: "Security is not a one-time check. Continuously validate and re-evaluate access throughout the session.",
  },
  {
    iconName: "CheckCircle",
    title: "Encrypt Everything",
    description: "Use encryption for all data in transit and at rest. Assume the network is hostile and protect data accordingly.",
  },
];

const comparisonData = [
  { aspect: "Trust Model", traditional: "Trust inside network perimeter", zeroTrust: "Never trust, always verify" },
  { aspect: "Access Control", traditional: "Network-based (VPN, firewall)", zeroTrust: "Identity-based (user, device, context)" },
  { aspect: "Verification", traditional: "Once at entry point", zeroTrust: "Continuous throughout session" },
  { aspect: "Default State", traditional: "Allow unless denied", zeroTrust: "Deny unless explicitly allowed" },
  { aspect: "Breach Impact", traditional: "Lateral movement possible", zeroTrust: "Contained to minimal scope" },
];

const faqItems = [
  {
    question: "Is zero-trust only for large enterprises?",
    answer: "No, zero-trust principles can benefit organizations of any size. While full implementation may be more complex for smaller teams, the core concepts of verification and least privilege can be adopted incrementally regardless of organization size.",
  },
  {
    question: "Does zero-trust replace firewalls?",
    answer: "Zero-trust doesn't eliminate firewalls but changes their role. Firewalls become one of many security layers rather than the primary trust boundary. They continue to provide value for network segmentation and traffic filtering.",
  },
  {
    question: "How does zero-trust affect user experience?",
    answer: "When implemented well, zero-trust can actually improve user experience by enabling secure access from anywhere without VPNs. Modern solutions use risk-based authentication that applies stronger verification only when warranted.",
  },
  {
    question: "What's the relationship between zero-trust and APIs?",
    answer: "APIs are critical in zero-trust architectures. Every API call must be authenticated and authorized. API gateways often serve as policy enforcement points, validating tokens, checking permissions, and logging access for every request.",
  },
  {
    question: "How long does it take to implement zero-trust?",
    answer: "Zero-trust is a journey, not a destination. Organizations typically start with identity management and gradually expand to include device trust, network segmentation, and continuous monitoring. A phased approach over months or years is common.",
  },
];

function ZeroTrustPage() {
  return (
    <div>
      <ContentHeader
        title="Zero-Trust Security"
        description="Zero-trust is a security model based on the principle of 'never trust, always verify.' It assumes that threats exist both inside and outside the network, requiring strict verification for every access request."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "Zero-Trust Security" },
        ]}
        badge="Concept"
      />

      {/* What is Zero Trust */}
      <ContentSection id="what-is-zero-trust" title="Understanding Zero-Trust">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Traditional security models operated on the assumption that everything inside the corporate
            network could be trusted. Once you passed the perimeter defenses—firewalls, VPNs, and
            access controls—you were generally free to move around.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            Zero-trust security eliminates this implicit trust. Every user, device, and connection must
            prove its legitimacy before accessing any resource. This approach recognizes that in modern
            environments—with cloud services, remote work, and mobile devices—the concept of a secure
            perimeter no longer applies.
          </p>

          <blockquote className="border-l-4 border-primary pl-4 mt-6 italic text-muted-foreground">
            "Never trust, always verify. Assume breach."
          </blockquote>
        </div>
      </ContentSection>

      {/* Core Principles */}
      <ContentSection
        id="principles"
        title="Core Principles"
        description="Zero-trust security is built on these foundational principles."
        background="muted"
      >
        <FeatureShowcase features={principles} columns={3} variant="cards" />
      </ContentSection>

      {/* Comparison */}
      <ContentSection
        id="comparison"
        title="Traditional vs. Zero-Trust Security"
        description="How zero-trust differs from traditional perimeter-based security."
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Aspect</th>
                <th className="py-4 px-4 text-left font-semibold">Traditional Security</th>
                <th className="py-4 px-4 text-left font-semibold bg-primary/5 text-primary">Zero-Trust</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, index) => (
                <tr key={index} className="border-b last:border-0">
                  <td className="py-4 px-4 font-medium">{row.aspect}</td>
                  <td className="py-4 px-4 text-muted-foreground">{row.traditional}</td>
                  <td className="py-4 px-4 bg-primary/5">{row.zeroTrust}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Implementation */}
      <ContentSection
        id="implementation"
        title="Implementing Zero-Trust"
        background="muted"
      >
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">1</div>
              <h3 className="font-semibold text-lg">Identify Your Protect Surface</h3>
            </div>
            <p className="text-muted-foreground">
              Start by identifying your most critical data, assets, applications, and services.
              Zero-trust focuses on protecting what matters most, not trying to secure everything equally.
            </p>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">2</div>
              <h3 className="font-semibold text-lg">Map Transaction Flows</h3>
            </div>
            <p className="text-muted-foreground">
              Understand how traffic moves across your network—who needs access to what, when, and why.
              This visibility is essential for designing appropriate access policies.
            </p>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">3</div>
              <h3 className="font-semibold text-lg">Build Your Zero-Trust Architecture</h3>
            </div>
            <p className="text-muted-foreground">
              Deploy identity management, micro-segmentation, and policy enforcement points.
              Use API gateways to control access to services and implement continuous monitoring.
            </p>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">4</div>
              <h3 className="font-semibold text-lg">Create Zero-Trust Policies</h3>
            </div>
            <p className="text-muted-foreground">
              Define who can access what resources, under what conditions, and for how long.
              Use attribute-based access control (ABAC) for fine-grained policies.
            </p>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">5</div>
              <h3 className="font-semibold text-lg">Monitor and Maintain</h3>
            </div>
            <p className="text-muted-foreground">
              Continuously monitor all traffic and log all access. Use analytics to detect anomalies
              and regularly review and update policies as your environment changes.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/rbac-abac"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">RBAC vs ABAC</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Access control models for zero-trust
            </p>
          </Link>
          <Link
            to="/concepts/api-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Policy enforcement for APIs
            </p>
          </Link>
          <Link
            to="/features/security"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Enterprise Security</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Matricks security features
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Implement Zero-Trust with Matricks"
        description="Matricks provides built-in zero-trust security with fine-grained policies, field-level access control, and comprehensive audit logging."
        primaryAction={{ label: "Get Started", href: "/login" }}
        secondaryAction={{ label: "View Security Features", href: "/features/security" }}
        background="primary"
      />
    </div>
  );
}
